import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner } from '../../components/ui';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError, downloadFile } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { arcaInvoiceStatus, dateLabel, fiscalInvoiceNo, money, num, customerShort } from '../../lib/format';
import type { ArcaInvoice, ArcaInvoicePayload, ListMeta } from '../../lib/types';
import { AttachmentPanel } from './AttachmentPanel';
import { FinanceGateNotice, isFinanceGateDisabled, useFinanceGate } from './FinanceGate';
import { describeInvoicePdfError, invoicePdfFilename, invoicePdfPath } from './invoicePdf';

// ARCA 税票 · 财务专页（应收口径，**受** INTEGRAL_ENABLE_FINANCE_READ 门控）。
//
// 为什么单独一页而不是塞回开票列表：本页的「已核销 / 未收余额 / 收款核销 / 原始报文 /
// 附件」都来自受门控的端点（/api/attachments*、/api/arca-invoices/:id/payloads、
// /api/payments）。门控在生产环境默认关闭 —— 若与发票身份字段同页，整页要么全关
// （丢掉可用的发票查询与 PDF 打印），要么半开（出现一堆 503 区块）。拆页后：
// 开票列表常开，本页随门控开关整体出现/隐藏，语义干净。
//
// 三处刻意留空，避免把未定口径做成既成事实：
//   · 部分开票（partially_invoiced）：合同 USD × 开票汇率 ↔ 发票 ARS 的核对规则待财务确认，
//     core-api 亦未实现（app.js 明文预留），本页不做推算；
//   · 已授权票据的更正/取消：Factura 侧走 ARCA 更正流程，本地不得抹除税务授权；
//   · 电子支票到账：不把「已登记收款」当成「已到账」。
function InvoiceDetail({ row }: { row: ArcaInvoice }) {
  const { user } = useAuth();
  const { data, loading, error } = useDetail<ArcaInvoice>(`/api/arca-invoices/${row.id}`);
  const invoice = data ?? row;

  const [payloads, setPayloads] = useState<ArcaInvoicePayload[]>([]);
  const [payloadMeta, setPayloadMeta] = useState<ListMeta | null>(null);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [openPayload, setOpenPayload] = useState<number | null>(null);

  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const downloadPdf = async () => {
    setPdfErr(null);
    setPdfBusy(true);
    try {
      await downloadFile(invoicePdfPath(invoice.id), invoicePdfFilename(invoice));
    } catch (e) {
      setPdfErr(describeInvoicePdfError(e));
    } finally { setPdfBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await api.get<{ data: ArcaInvoicePayload[]; meta: ListMeta }>(`/api/arca-invoices/${row.id}/payloads`);
        if (cancelled) return;
        setPayloads(body.data || []);
        setPayloadMeta(body.meta || null);
        setPayloadError(null);
      } catch (e) {
        if (cancelled) return;
        if (isFinanceGateDisabled(e)) setPayloadError('本环境未启用原始报文留存');
        else setPayloadError(e instanceof ApiError ? e.message : '原始报文读取失败');
        setPayloads([]);
      }
    })();
    return () => { cancelled = true; };
  }, [row.id]);

  const st = arcaInvoiceStatus(invoice.status);

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        {/* 对外标识统一用税务局票号；内部编号只在 title 里留作排查线索。 */}
        <span className="id" title={invoice.internal_number ? `内部编号 ${invoice.internal_number}` : ''}>
          {fiscalInvoiceNo(invoice)}
        </span>
        <h3>{invoice.customer_name || invoice.customer_id}</h3>
        <Pill tone={st.tone} label={st.label} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
        <button className="btn" disabled={!invoice.cae || pdfBusy} onClick={() => void downloadPdf()}
                title={invoice.cae ? '下载三联发票 PDF（原票扫描/渲染件）' : '无 CAE，无法定位税务 PDF'}>
          {pdfBusy ? '下载中…' : '三联发票 PDF'}
        </button>
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {pdfErr ? <div className="notice bad">{pdfErr}</div> : null}

      <div className="kv-grid">
        <KeyValue k="票种">{invoice.voucher_type}</KeyValue>
        <KeyValue k="销售点 / 凭证号">{`${invoice.point_of_sale || '—'} · ${invoice.voucher_number ?? '—'}`}</KeyValue>
        <KeyValue k="开票日期">{dateLabel(invoice.invoice_date)}</KeyValue>
        <KeyValue k="到期日">{dateLabel(invoice.due_date)}</KeyValue>
        <KeyValue k="合同">{invoice.contract_number || invoice.contract_id || '—'}</KeyValue>
        <KeyValue k="客户税号">{invoice.customer_tax_id || '—'}</KeyValue>
        <KeyValue k="币种 / 汇率">{`${invoice.currency} · ${num(invoice.exchange_rate, 6)}`}</KeyValue>
        <KeyValue k="净额 / 税额">{`${money(invoice.net_total, invoice.currency)} / ${money(invoice.tax_total, invoice.currency)}`}</KeyValue>
        <KeyValue k="总额（原币）">{money(invoice.grand_total, invoice.currency)}</KeyValue>
        <KeyValue k="总额（ARS）">{money(invoice.grand_total_ars, 'ARS')}</KeyValue>
        <KeyValue k="已核销收款">{money(invoice.paid_amount, 'ARS')}</KeyValue>
        <KeyValue k="未收余额">{money(invoice.outstanding_amount, 'ARS')}</KeyValue>
        <KeyValue k="CAE">{invoice.cae || '—'}</KeyValue>
        <KeyValue k="CAE 到期">{dateLabel(invoice.cae_due_date)}</KeyValue>
        <KeyValue k="来源">{`${invoice.source_system || '—'} · ${invoice.source_id || '—'}`}</KeyValue>
        <KeyValue k="二维码">{invoice.qr_url || '—'}</KeyValue>
      </div>

      <div className="muted" style={{ fontSize: 12.5, margin: '6px 0 12px' }}>
        未收余额 = ARS 总额 − 已登记收款核销额。它**不代表**「已到账/未到账」——
        电子支票等收款方式的实际到账日期与状态尚未在本系统落库（待财务确认口径）。
        部分开票金额判定、已授权票据的更正与取消同样未实现，本页不作推算。
      </div>

      <PayloadSection
        payloads={payloads} meta={payloadMeta} error={payloadError}
        openId={openPayload} onToggle={setOpenPayload}
      />

      <div style={{ fontWeight: 650, marginBottom: 6 }}>附件（原件登记）</div>
      <AttachmentPanel entityType="arca_invoice" entityId={invoice.id} canUpload={can(user, 'attachments', 'create')} />
    </div>
  );
}

// 原始报文列表：同一张发票可能有多份 draft/result（历史版本不被覆盖），
// 因此这里按「收到时间」逐条列出，而不是只显示最后一版。
function PayloadSection({ payloads, meta, error, openId, onToggle }: {
  payloads: ArcaInvoicePayload[];
  meta: ListMeta | null;
  error: string | null;
  openId: number | null;
  onToggle: (id: number | null) => void;
}) {
  const opened = payloads.find((p) => p.id === openId) || null;
  let openedJson = '';
  if (opened) {
    try { openedJson = JSON.stringify(JSON.parse(opened.payload_json), null, 2); }
    catch { openedJson = opened.payload_json; }
  }
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontWeight: 650, marginBottom: 6 }}>
        原始报文留存 {meta ? `（${meta.total} 条）` : ''}
      </div>
      {error ? <div className="notice warn">{error}</div> : payloads.length === 0 ? (
        <div className="muted" style={{ fontSize: 12.5 }}>暂无原始报文记录。</div>
      ) : (
        <table className="lines">
          <thead>
            <tr>
              <th>类型</th><th>来源编号</th><th>收到时间</th><th>sha256（前16位）</th>
              <th style={{ textAlign: 'right' }}>原文</th>
            </tr>
          </thead>
          <tbody>
            {payloads.map((p) => (
              <tr key={p.id}>
                <td>{p.kind === 'draft' ? '草稿（draft）' : '授权结果（result）'}</td>
                <td>{p.source_id}</td>
                <td>{String(p.received_at).slice(0, 19)}</td>
                <td className="num">{String(p.sha256).slice(0, 16)}…</td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn" onClick={() => onToggle(openId === p.id ? null : p.id)}>
                    {openId === p.id ? '收起' : '查看'}
                  </button>
                </td>
              </tr>
            ))}
            {opened ? (
              <tr>
                <td colSpan={5}>
                  <pre style={{ maxHeight: 260, overflow: 'auto', fontSize: 12 }}>{openedJson}</pre>
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * 页面入口（财务专页）。
 *
 * 门控状态统一走 useFinanceGate()（与导航、收款页同源），**不用本页端点自己探**：
 * 门控关闭时 GET /api/arca-invoices 为了向后兼容仍返回旧信封 200，
 * 拿它探会把「本环境未启用」误判成「已启用」，于是用户看到一张空表还以为功能坏了。
 */
export function ArcaFinancePage() {
  // 开票列表详情里的「财务专页（应收/核销/原始报文）」会带 ?q=<内部编号> 跳进来，
  // 从合同点进来的 ?q=<合同号> 也照旧生效：两种都是该列表能匹配的关键字。
  const [sp] = useSearchParams();
  // 行内 PDF 下载的失败提示放在页面顶部：按钮所在行可能已被滚动出视野。
  // 本页虽受门控，但 PDF 端点本身不受门控 —— 门控开着时下载失败原因仍按 code 如实说明。
  // 注意：所有 Hook 必须在下面的门控早退**之前**调用，否则 gate 从 unknown 变 enabled 时
  // Hook 数量变化会让 React 报「Rendered fewer hooks than expected」。
  const [pdfErr, setPdfErr] = useState<string | null>(null);
  const gate = useFinanceGate();

  const downloadRow = async (r: ArcaInvoice) => {
    setPdfErr(null);
    try {
      await downloadFile(invoicePdfPath(r.id), invoicePdfFilename(r));
    } catch (e) {
      setPdfErr(`${fiscalInvoiceNo(r)}：${describeInvoicePdfError(e)}`);
    }
  };

  if (gate === 'disabled') return <FinanceGateNotice />;
  if (gate !== 'enabled') return <Spinner label="正在确认本环境财务能力…" />;

  const columns: Column<ArcaInvoice>[] = [
    // 税务局票号是唯一对外标识（现场口径：内部发票号不再采用，不再单列一列）。
    // 内部编号降级到 title，仅供排查时对照。
    { header: '税务局票号', render: (r) => (
      <span className="num strong" title={r.internal_number ? `内部编号 ${r.internal_number}` : ''}>
        {fiscalInvoiceNo(r)}
      </span>
    ) },
    { header: '票种', render: (r) => r.voucher_type },
    { header: '开票日期', render: (r) => dateLabel(r.invoice_date) },
    { header: '客户', render: (r) => <span title={r.customer_name || ''}>{customerShort(r.customer_name || r.customer_id)}</span> },
    { header: '合同', render: (r) => r.contract_number || '—' },
    { header: '汇率', align: 'right', render: (r) => num(r.exchange_rate, 4) },
    { header: '总额 ARS', align: 'right', render: (r) => money(r.grand_total_ars, 'ARS') },
    { header: '已核销', align: 'right', render: (r) => money(r.paid_amount, 'ARS') },
    { header: '未收余额', align: 'right', render: (r) => money(r.outstanding_amount, 'ARS') },
    { header: '状态', render: (r) => { const s = arcaInvoiceStatus(r.status); return <Pill tone={s.tone} label={s.label} />; } },
  ];

  return (
    <>
      {pdfErr ? <div className="notice bad" style={{ marginBottom: 'var(--gap)' }}>{pdfErr}</div> : null}
      <ListPage<ArcaInvoice>
        title="ARCA 税票 · 财务专页"
        desc="应收/收款/原始报文；受 INTEGRAL_ENABLE_FINANCE_READ 控制，关闭时本页不可用。金额为发票 ARS 口径，与合同 USD 金额分开保存。"
        endpoint="/api/arca-invoices"
        rowId={(r) => r.id}
        columns={columns}
        rowActions={(r) => (
          <button
            className="btn link"
            disabled={!r.cae}
            title={r.cae ? `下载 ${fiscalInvoiceNo(r)} 的三联发票 PDF` : '无 CAE，无法定位税务 PDF'}
            onClick={(e) => {
              // 行点击会展开详情；下载按钮必须阻断冒泡，否则点一次既下载又展开。
              e.stopPropagation();
              void downloadRow(r);
            }}
          >
            三联发票 PDF
          </button>
        )}
        renderDetail={(row) => <InvoiceDetail row={row} />}
        searchPlaceholder="搜索税务局票号 / CAE / 客户 / 合同号 / 客户合同号…"
        initialQuery={sp.get('q') || ''}
      />
    </>
  );
}
